import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { Document } from 'src/services/documents/types';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface GetIncomeEligibleDocumentsParams {
  loanId: string;
}

export interface GetIncomeEligibleDocumentsResponse {
  success: boolean;
  documents: Document[];
  nextCursor?: string;
}

// Helper function to reduce duplication
const createMockIncomeEligibleDocument = (
  id: string,
  name: string,
  confidence: number,
  documentTypeId: string,
  filename: string,
  documentTypeName: string,
  displayName: string,
): Document => ({
  id,
  name,
  confidence,
  version: 0,
  versionIdentifier: 'final' as const,
  pages: [],
  fields: [],
  documentTypeId,
  metadata: {
    external_document_filename: filename,
  },
  isNew: false,
  isVerified: true,
  isViewed: true,
  userApproved: true,
  userTasks: [],
  documentType: {
    id: documentTypeId,
    name: documentTypeName,
    displayName,
    fields: [],
    filenamePrefix: null,
    classificationReviewThreshold: 0,
    extractionReviewThreshold: 0,
    borrowerReviewThreshold: 0,
    highConfThresh: 0,
  },
});

const getIncomeEligibleDocuments: QueryFunction<
  GetIncomeEligibleDocumentsResponse,
  ['/income-eligible-documents', GetIncomeEligibleDocumentsParams]
> = async ({ queryKey }) => {
  if (isMockServices()) {
    return await mock({
      success: true,
      documents: [
        createMockIncomeEligibleDocument(
          'doc-1',
          'W-2 Form 2023',
          95,
          'w2-type',
          'w2-2023.pdf',
          'W-2',
          'W-2 Form',
        ),
        createMockIncomeEligibleDocument(
          'doc-2',
          'Paystub December 2023',
          92,
          'paystub-type',
          'paystub-dec-2023.pdf',
          'Paystub',
          'Paystub',
        ),
      ],
      nextCursor: undefined,
    });
  }

  const params = queryKey[1];
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<GetIncomeEligibleDocumentsResponse>(
    `/loans/${params.loanId}/income-eligible-documents`,
    {
      method: 'GET',
    },
  );

  if (response.data) return response.data;
  throw new Error('Failed to fetch income eligible documents');
};

export default getIncomeEligibleDocuments;
